import {Tabs, TabsContent, TabsList, TabsTrigger} from "@components/shadcn-ui/tabs";
import {useEffect, useState, useMemo} from "react";
import {Task, TaskStatus} from "features/tasks/types";
import {getAssignedTasks, getCreatedTasks} from "features/tasks/services/tasksService";
import {TaskList} from "features/tasks/components/TaskList";
import {TaskTools} from "features/tasks/components/TaskTools";
import TableShimmer, {CardShimmer} from "features/tasks/components/TaskShimmer";
import {MobileTaskCard} from "../../features/tasks/components/MobileTaskCard";

export const TaskListPage = () => {
    const [activeTab, setActiveTab] = useState<"assigned" | "created">("assigned");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedStatus, setSelectedStatus] = useState<TaskStatus | null>(TaskStatus.OPEN);
    const [isLoading, setIsLoading] = useState(true);

    // Separate state for assigned and created tasks
    const [assignedTasks, setAssignedTasks] = useState<Task[]>([]);
    const [createdTasks, setCreatedTasks] = useState<Task[]>([]);

    // Track if data has been fetched for each tab
    const [dataFetched, setDataFetched] = useState({
        assigned: false,
        created: false
    });

    const currentUser = JSON.parse(localStorage.getItem("userDetails") as string).userName;

    useEffect(() => {
        const fetchTasks = async () => {
            // Only fetch if we haven't already fetched data for this tab
            if (!dataFetched[activeTab]) {
                setIsLoading(true);
                try {
                    if (activeTab === "assigned") {
                        const response = await getAssignedTasks(currentUser);
                        setAssignedTasks(response);
                    } else {
                        const response = await getCreatedTasks(currentUser);
                        setCreatedTasks(response);
                    }
                    // Mark this tab's data as fetched
                    setDataFetched(prev => ({
                        ...prev,
                        [activeTab]: true
                    }));
                } catch (error) {
                    console.error("Failed to fetch tasks:", error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchTasks();
    }, [activeTab, currentUser, dataFetched]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleStatusChange = (status: TaskStatus | "all") => {
        setSelectedStatus(status === "all" ? null : status);
    };

    // Use the appropriate task list based on active tab
    const currentTasks = activeTab === "assigned" ? assignedTasks : createdTasks;

    const filteredTasks = useMemo(() => {
        return currentTasks.filter((task) => {
            const matchesStatus = selectedStatus === null || task.status === selectedStatus;
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch =
                task.title.toLowerCase().includes(searchLower) ||
                task.description.toLowerCase().includes(searchLower) ||
                task.taskId.toLowerCase().includes(searchLower) ||
            task.assignee.toLowerCase().includes(searchLower) ||
            task.author.toLowerCase().includes(searchLower) ||
            task.resourceId.toLowerCase().includes(searchLower) ||
            task.resourceType.toLowerCase().includes(searchLower);

            return matchesStatus && matchesSearch;
        });
    }, [currentTasks, selectedStatus, searchQuery]);

    return (
        <>
            <div className="mb-4">
                <h1 className="text-2xl font-bold">Tasks</h1>
                <p className="text-sm text-gray-500">
                    View and manage your tasks in one place.
                </p>
            </div>

            <Tabs defaultValue="assigned" className="w-full" onValueChange={(value) => setActiveTab(value as "assigned" | "created")}>
                <TabsList className="mb-4">
                    <TabsTrigger value="assigned">Assigned to Me</TabsTrigger>
                    <TabsTrigger value="created">Created by Me</TabsTrigger>
                </TabsList>

                <div className="mb-4">
                    <TaskTools
                        searchQuery={searchQuery}
                        handleSearchChange={handleSearchChange}
                        selectedStatus={selectedStatus}
                        handleStatusChange={handleStatusChange}
                    />
                </div>

                {["assigned", "created"].map((tab) => (
                    <TabsContent key={tab} value={tab} className="mt-0">
                        <div className="hidden md:block">
                            {isLoading && !dataFetched[tab as keyof typeof dataFetched] ? (
                                <TableShimmer />
                            ) : filteredTasks.length > 0 ? (
                                <TaskList tasks={filteredTasks} />
                            ) : (
                                <p className="text-center bg-transparent">No tasks found.</p>
                            )}
                        </div>
                        <div className="md:hidden">
                            {isLoading && !dataFetched[tab as keyof typeof dataFetched] ? (
                                <CardShimmer />
                            ) : filteredTasks.length > 0 ? (
                                <MobileTaskCard tasks={filteredTasks}/>
                            ) : (
                                <p className="text-center">No tasks found.</p>
                            )}
                        </div>
                    </TabsContent>
                ))}
            </Tabs>
        </>
    );
};
